<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";

// Composables
import { useWorkerStatusActions } from "@/composables/missions/worker-status-actions/useWorkerStatusActions";
import { formatRelativeTime } from "@/helpers/date";
import { formatPhone } from "@/helpers/phone";
// Helpers
import { getWorkerMissionStatusColor, getWorkerMissionStatusLabel } from "@/modules/companyNeed/helpers/workerMissionStatus";
import { NeedRecruitmentCardAlerts } from "@/modules/companyNeed/types/recruitment";
import { WorkerMissionStatus } from '@/types/mission';

// Types
import type { MissionWorker } from "@/modules/companyNeed/types/missions";

import { type AvatarProps, AvatarSize } from "@/dsComponents/Avatar";
// DS Components
import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import ProgressCircle from "@/dsComponents/progress/ProgressCircle.vue";
import Separator from "@/dsComponents/Separator.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

// Components
import WorkerStatusActions from "@/components/Mission/WorkerStatusActions.vue";
import WorkerAbortReasonTooltip from "@/modules/companyNeed/components/modals/mission/propositions/WorkerAbortReasonTooltip.vue";
import WorkerIncompleteRegistrationAlert from "@/modules/companyNeed/components/modals/mission/propositions/WorkerIncompleteRegistrationAlert.vue";

interface WorkersPropositionsByStatusProps {
    status: WorkerMissionStatus;
    positionQuantity: number;
    workers: MissionWorker[];
    isOpen?: boolean;
    isLast?: boolean;
}

const props = defineProps<WorkersPropositionsByStatusProps>();
const emits = defineEmits<{
    (event: "toggleShow", status: WorkerMissionStatus): void;
    (event: "openWorkerDetail", workerId: string): void;
    (event: "onWorkerStatusChange", workerId: string, newStatus: WorkerMissionStatus): void;
}>();

const { workerStatusActionsProvider } = useWorkerStatusActions();
const workerIdSelected = ref<string>();

const isConfirmedStatusSection = computed(() => {
    return props.status === WorkerMissionStatus.CONFIRMED;
});

const confirmedWorkerRatio = computed(() => {
    const confirmedWorkers = props.workers.filter((worker) => worker.history.some((h) => h.status === WorkerMissionStatus.CONFIRMED)).length;
    if (!confirmedWorkers) return 0;
    return (confirmedWorkers / props.positionQuantity) * 100;
});

const tableAltColumns = [
    {
        field: "name",
    },
    {
        field: "alert",
        width: "22px",
    },
    {
        field: "lastupdate",
        width: "90px",
    },
    {
        field: "phone",
        width: "100px",
        displayOnHover: true,
    },
];

const globalActions = computed(() => {
    if (props.status === WorkerMissionStatus.NOT_CONFIRMED) return undefined;

    const availableStatuses = [...new Set(props.workers.flatMap((w) => w.availableStatuses))];

    return {
        width: "28px",
        rowActions: [
            {
                component: WorkerStatusActions,
                props: {
                    availableStatuses: availableStatuses,
                },
                action: (index: number) => {
                    workerIdSelected.value = props.workers[index]?.id;
                },
            },
        ],
    };
});

const formatedData = computed(() => {
    return props.workers.map((worker, rowIndex) => {
        const hasIncompleteRegistration = worker.alerts?.includes(NeedRecruitmentCardAlerts.INCOMPLETE_REGISTRATION);
        const abortReason =
            props.status === WorkerMissionStatus.ABORTED
                ? worker.history.find((h) => h.status === WorkerMissionStatus.ABORTED)?.reason
                : undefined;

        const content = {
            name: {
                customElement: {
                    component: Avatar,
                    props: {
                        size: AvatarSize.size16,
                        firstname: worker.firstname,
                        lastname: worker.lastname,
                        displayName: true,
                        fontSize: "12px",
                    } as AvatarProps,
                },
            },
            alert: hasIncompleteRegistration
                ? {
                      customElement: {
                          component: WorkerIncompleteRegistrationAlert,
                          props: {},
                      },
                  }
                : {},
            lastupdate: abortReason
                ? {
                      customElement: {
                          component: WorkerAbortReasonTooltip,
                          props: {
                              date: formatRelativeTime(worker.lastupdate),
                              reason: abortReason,
                          },
                      },
                  }
                : {
                      text: {
                          value: formatRelativeTime(worker.lastupdate),
                          passive: true,
                          customClass: "text-vertical-align-bottom lastupdate-cell",
                      },
                  },
            phone: {
                text: {
                    value: formatPhone(worker.phone),
                    passive: true,
                    customClass: "text-vertical-align-bottom",
                },
            },
        };

        return {
            content,
            editMode: false,
            rowIndex,
        };
    });
});

const toggleShowTableSection = () => {
    emits("toggleShow", props.status);
};

const handleRowClick = (index: number) => {
    const workerId = props.workers[index]?.id;
    if (workerId) {
        emits("openWorkerDetail", workerId);
    }
};

const handleAction = (status: WorkerMissionStatus) => {
    if (!workerIdSelected.value) return;

    // Special case: workerDetails opens the detail modal
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    if (status === ("workerDetails" as any)) {
        emits("openWorkerDetail", workerIdSelected.value);
        return;
    }

    // Emit the status change event
    emits("onWorkerStatusChange", workerIdSelected.value, status);
};

onBeforeMount(() => {
    workerStatusActionsProvider(handleAction);
});
</script>
<template>
    <div>
        <div class="worker-propositions-by-status">
            <div class="section-title">
                <div class="section-title-and-count">
                    <Button
                        v-if="isConfirmedStatusSection"
                        size="sm"
                        type="neutral"
                        variant="ghost"
                        :leftIcon="{ icon: 'status-marker', color: getWorkerMissionStatusColor(status) }"
                        :label="getWorkerMissionStatusLabel(status, workers.length)"
                    />
                    <Button
                        v-else
                        size="sm"
                        variant="ghost"
                        type="neutral"
                        :leftIcon="{ icon: 'status-marker', color: getWorkerMissionStatusColor(status) }"
                        :rightIcon="isOpen ? 'caret-down-filled' : 'caret-right-filled'"
                        @click="toggleShowTableSection"
                        :label="getWorkerMissionStatusLabel(status, workers.length)"
                    />
                    <ProgressCircle
                        v-if="isConfirmedStatusSection"
                        :fill="confirmedWorkerRatio"
                        :checked="confirmedWorkerRatio >= 100"
                        :labelRight="`${workers.length}/${positionQuantity}`"
                    />
                    <p v-else class="section-count typo-xxs-regular">{{ workers.length }}</p>
                </div>
            </div>
            <div v-if="isOpen && formatedData.length">
                <TableAlt
                    rowHover
                    :columns="tableAltColumns"
                    :rows="formatedData"
                    :globalActions="globalActions"
                    @action="handleAction"
                    rowClick
                    @rowClick="handleRowClick"
                />
            </div>
        </div>
        <Separator v-if="!isLast" />
    </div>
</template>
<style scoped lang="scss">
.worker-propositions-by-status {
    padding: var(--spacing-3xl, 24px) var(--spacing-lg, 12px);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);

    .section-title {
        display: flex;
        height: 28px;
        justify-content: space-between;

        .section-title-and-count {
            display: flex;
            gap: var(--spacing-md, 8px);
            align-items: center;
        }
    }
}

.section-count {
    color: var(--text-sub-600, #4c505e);
}

:deep(.lastupdate-cell) {
    color: var(--text-sub-600, #4c505e);
}
</style>
